import Link from "next/link";
import router, { useRouter } from "next/router";
import {
  CSSProperties,
  PropsWithChildren,
  ReactNode,
  useState,
  MouseEvent,
  useRef,
  useEffect,
  useCallback,
} from "react";
import {
  Button,
  Dropdown,
  Popconfirm,
  Space,
  Spin,
  Tooltip,
  theme,
  Modal,
  Progress,
} from "antd";
import { signOut, useSession } from "next-auth/react";
import {
  AppstoreFilled,
  AppstoreOutlined,
  CaretDownFilled,
  CaretRightFilled,
  CloseOutlined,
  CompassFilled,
  CompassOutlined,
  CopyOutlined,
  FileTextFilled,
  FileTextOutlined,
  HomeFilled,
  HomeOutlined,
  LogoutOutlined,
  NumberOutlined,
  SearchOutlined,
  SettingOutlined,
  WifiOutlined,
  TeamOutlined,
  PlusCircleOutlined,
  ProfileFilled,
  ProfileOutlined,
  QuestionCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useQuery } from "react-query";
import {
  isSessionable,
  TabLink,
  useApplicationContext,
} from "src/hooks/useApplication";
import { Flex } from "src/components/Flex";
import { Hover } from "src/components/Hover";
import { SearchState } from "src/search/types";
import { Notifications } from "src/components/Notifications";
import {
  ensureState,
  serializeParams,
} from "src/search/lib/params-state-reducer";
import { SidebarIcon } from "src/components/SidebarIcon";
import { useSidebar } from "src/hooks/useSidebar";
import { ModalLibrarySearch } from "src/components/LibrarySearch";
import { SparkleIcon } from "src/components/SparkleIcon";
import { serializeDocument } from "./[orgId]/explore/lib/useExploreDocument";
import {
  ContentViews,
  SerializableView,
  serializeContentView,
} from "./[orgId]/explore/lib/useContentView";
import { serializeTabs } from "./[orgId]/explore/lib/useTabs";
import { GlobalFunction, globalFunction } from "./[orgId]/global-functions";
import { ExploreContext } from "./[orgId]/explore/useExploreContext";
import { OrgLauncher } from "./OrgLauncher";
import { getMyOrgs } from "./api-clients/orgs";
import { useSpacesQuery } from "./[orgId]/space/useSpacesQuery";
import { COLLAPSED_MENU_WIDTH, MENU_WIDTH } from "./Menu";
import { MENU_TYPE } from "src/hooks/useMenu";
import { BookClosedIcon } from "src/components/BookClosedIcon";
import { useDeepLinks } from "src/hooks/useDeepLinks";
import { DeepLinkType } from "pages/api/deep-links/types";
import {
  useLibraryWithoutRouter,
  ItemType,
  Item,
  BoardItem,
  QueryItem,
  TopicItem,
  ReportItem,
} from "src/components/useLibrary";
import { FavoritesMenu } from "./FavoritesMenu";
import { CssVars } from "src/components/CssVars";
import { VzblIcon } from "src/components/VzblIcon";
import { SearchModal } from "src/components/SearchModal";
import { useIntegrations } from "src/search/lib/useIntegrations";
import { getCreateItems } from "src/components/CreateButton";
import { SlackIcon } from "src/components/SlackIcon";
import { TrialProgressBar } from "src/components/TrialProgressBar";
import { OrgLauncherNew } from "./OrgLauncherNew";

export function MainMenu({
  orgId,
  loading,
  setMenu,
}: {
  orgId?: string;
  loading: boolean;
  setMenu: (menu: MENU_TYPE) => void;
}) {
  const { status } = useSession();
  const { token } = theme.useToken();
  const session = useSession();
  const disabled = !orgId || status !== "authenticated";
  const app = useApplicationContext();
  const [isHovered, setIsHovered] = useState(false);
  const boards = useDeepLinks(DeepLinkType.BOARD);

  // Check if subscription is expired or canceled
  const isSubscriptionExpired =
    app.subscription?.status === "expired" ||
    app.subscription?.status === "canceled" ||
    (app.subscription?.status === "trialing" &&
      app.subscription?.trialEndDate &&
      new Date(app.subscription.trialEndDate) < new Date());

  const { asPath } = useRouter();
  const pathname = asPath
    .replaceAll("[orgId]", orgId || "")
    .split("?")
    .at(0);
  const matchUrl = (pathPart: string, options?: { prefix: boolean }) =>
    options?.prefix
      ? pathname?.startsWith(`/${orgId}/${pathPart}`)
      : pathname === `/${orgId}/${pathPart}`;

  const orgCount =
    useQuery([], () => getMyOrgs(), { enabled: Boolean(orgId) }).data?.data
      ?.length || 0;

  const [showingOrgs, showOrgs] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const org_id = app.org_id;
  const topics = useDeepLinks(DeepLinkType.DIGEST_2)?.links?.data;

  const pinnedPanel = useSidebar("app-menu-pinned-panel", true);
  const sessionPanel = useSidebar("app-menu-session-panel", true);

  const query = useSpacesQuery();
  const integrations = useIntegrations({ orgId: org_id });
  const createItems = getCreateItems({ app, boards, query });

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1100px)");
    const handler = (e: MediaQueryListEvent) => {
      if (e.matches && app.sidebar.isOpen) {
        app.sidebar.toggle();
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [app.sidebar]);

  return (
    <CssVars>
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Flex
          centered
          className="main-menu"
          style={{ position: "relative", color: token.colorTextLightSolid }}
        >
          <div
            className="js-menu menu-container"
            style={{
              width: app.sidebar.isOpen ? MENU_WIDTH : COLLAPSED_MENU_WIDTH,
              display: "flex",
              flexDirection: "column",
              height: "100vh",
            }}
          >
            <div style={{ flex: 0, height: 48 }}>
              <MainButton
                loading={loading}
                showCollapsible={app.sidebar.isOpen ? isHovered : true}
              />
            </div>
            <div
              style={{
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {app.sidebar.isOpen && <TrialProgressBar orgId={orgId} />}
              {!isSubscriptionExpired && (
                <Tooltip
                  title="Search or Ask"
                  placement="right"
                  mouseEnterDelay={app.sidebar.isOpen ? 60 : 0}
                  open={app.sidebar.isOpen ? false : undefined}
                >
                  <Button
                    type="text"
                    className="main-menu-search-button"
                    icon={<SearchOutlined />}
                    onClick={() => setShowSearchModal(true)}
                  >
                    {app.sidebar.isOpen && <span>Search or Query</span>}
                  </Button>
                </Tooltip>
              )}

              <MenuLink
                root
                isActive={matchUrl("home")}
                icon={matchUrl("home") ? <HomeFilled /> : <HomeOutlined />}
                disabled={disabled || isSubscriptionExpired}
                label="Home"
                href={`/${orgId}/home`}
              />
              {app.isSuperAdmin && (
                <MenuLink
                  root
                  isActive={matchUrl("users", { prefix: true })}
                  icon={<ProfileOutlined />}
                  label="Profile"
                  href={`/${orgId}/users/${session.data?.user.id}`}
                  disabled={isSubscriptionExpired}
                />
              )}
              <MenuLink
                root
                isActive={matchUrl("teams", { prefix: true })}
                icon={<TeamOutlined />}
                label="Teams"
                href={`/${orgId}/teams`}
                disabled={isSubscriptionExpired}
              />
              <MenuLink
                root
                isActive={matchUrl("reports/list")}
                icon={<BookClosedIcon />}
                disabled={disabled || isSubscriptionExpired}
                label="Reports"
                href={`/${orgId}/reports/list`}
              />
              <MenuLink
                root
                isActive={matchUrl("feed")}
                icon={<WifiOutlined />}
                disabled={disabled || isSubscriptionExpired}
                label="Feed"
                href={`/${orgId}/feed`}
              />
              <MenuLink
                root
                isActive={matchUrl("library")}
                icon={
                  matchUrl("library") ? <CompassFilled /> : <CompassOutlined />
                }
                disabled={disabled || isSubscriptionExpired}
                label="Library"
                href={`/${orgId}/library`}
              />

              {orgId && (
                <div style={{ marginTop: 0, marginBottom: 48, padding: 0 }}>
                  <FavoritesMenuWrapper
                    orgId={orgId}
                    disabled={isSubscriptionExpired}
                  />
                </div>
              )}

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: pinnedPanel.isOpen ? 0 : 8,
                }}
              >
                <Tooltip
                  title={
                    isSubscriptionExpired
                      ? "Subscription expired"
                      : "Toggle workspace"
                  }
                  placement="right"
                  open={app.sidebar.isOpen ? false : undefined}
                  mouseEnterDelay={app.sidebar.isOpen ? 60 : 0}
                >
                  <div
                    style={{
                      display: "flex",
                      paddingLeft: 13,
                      paddingBottom: 3,
                      paddingTop: 8,
                      fontSize: 11,
                      fontWeight: 600,
                      cursor: isSubscriptionExpired ? "not-allowed" : "pointer",
                      gap: 12,
                      alignItems: "flex-end",
                      opacity: isSubscriptionExpired ? 0.3 : 0.6,
                      flex: 1,
                    }}
                    onClick={() =>
                      !isSubscriptionExpired && sessionPanel.toggle()
                    }
                  >
                    <div>
                      {sessionPanel.isOpen ? (
                        <CaretDownFilled />
                      ) : (
                        <CaretRightFilled />
                      )}
                    </div>
                    {app.sidebar.isOpen && (
                      <span style={{ flex: 1 }} className="trunc">
                        WORKSPACE
                      </span>
                    )}
                  </div>
                </Tooltip>

                <Dropdown
                  menu={{
                    items: createItems,
                  }}
                  trigger={["hover", "click"]}
                  mouseEnterDelay={0.3}
                  disabled={isSubscriptionExpired}
                >
                  <span style={{ position: "relative" }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      style={{
                        borderRadius: "50%",
                        height: 36,
                        width: 36,
                        justifyContent: "center",
                        gap: 4,
                        boxShadow: "0 2px 0 rgba(0, 0, 0, 0.02)",
                        color: "inherit",
                        opacity: isSubscriptionExpired ? 0.3 : 0.8,
                        ...(app.sidebar.isOpen
                          ? {
                              marginRight: 8,
                              marginTop: 6,
                            }
                          : {
                              position: "absolute",
                              left: "50%",
                              transform: "translateX(-50px)",
                              top: -40,
                            }),
                      }}
                      size="small"
                      shape="circle"
                      disabled={isSubscriptionExpired}
                    />
                  </span>
                </Dropdown>
              </div>

              {sessionPanel.isOpen && (
                <div
                  className="section-divider"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    opacity: isSubscriptionExpired ? 0.3 : 1,
                    pointerEvents: isSubscriptionExpired ? "none" : "auto",
                  }}
                >
                  {app.tabs.map((tab) => (
                    <SidebarItem
                      key={tab.id}
                      isActive={
                        app.activeTabId === tab.id &&
                        isSessionable(asPath, orgId as string)
                      }
                      disabled={isSubscriptionExpired}
                      style={{ paddingTop: 0, paddingBottom: 0 }}
                    >
                      <div
                        className="workspace-tab-row"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          position: "relative",
                          width: "100%",
                        }}
                      >
                        <Link
                          href={{
                            pathname: "/[orgId]/t/[tabId]",
                            query: {
                              orgId,
                              tabId: tab.id,
                            },
                          }}
                          style={{
                            flex: 1,
                            textDecoration: "none",
                            display: "block",
                            height: "100%",
                            width: "100%",
                            color:
                              app.activeTabId === tab.id &&
                              isSessionable(asPath, orgId as string)
                                ? token.colorWhite
                                : "inherit",
                          }}
                          onClick={(e: React.MouseEvent) => {
                            console.log("Workspace tab clicked:", {
                              tabId: tab.id,
                              activeTabId: app.activeTabId,
                            });
                            if (
                              app.activeTabId === tab.id &&
                              isSessionable(asPath, orgId as string)
                            ) {
                              console.log(
                                "Preventing navigation for active tab:",
                                tab.id
                              );
                              e.preventDefault();
                              e.stopPropagation();
                            } else {
                              console.log(
                                "Allowing navigation for tab:",
                                tab.id
                              );
                            }
                          }}
                        >
                          <Flex
                            gap={8}
                            style={{
                              padding: "4px 8px 4px 8px",
                              cursor: "pointer",
                              justifyContent: "flex-start",
                              alignItems: "center",
                              height: "100%",
                            }}
                          >
                            <Flex centered style={{ width: 20, opacity: 0.6 }}>
                              {app.meta.tabSessionTypes[tab.id] ===
                              TabLink.BOARDS ? (
                                app.activeTabId === tab.id &&
                                isSessionable(asPath, orgId as string) ? (
                                  <AppstoreFilled />
                                ) : (
                                  <AppstoreOutlined />
                                )
                              ) : app.meta.tabSessionTypes[tab.id] ===
                                TabLink.ASK ? (
                                <SparkleIcon />
                              ) : app.meta.tabSessionTypes[tab.id] ===
                                TabLink.ENTITY ? (
                                <NumberOutlined />
                              ) : app.activeTabId === tab.id &&
                                isSessionable(asPath, orgId as string) ? (
                                <FileTextFilled />
                              ) : (
                                <FileTextOutlined />
                              )}
                            </Flex>
                            {app.sidebar.isOpen && (
                              <span
                                style={{
                                  flex: 1,
                                  minWidth: 0,
                                  paddingRight: 10,
                                }}
                              >
                                {tab.current_title || "n/a"}
                              </span>
                            )}
                          </Flex>
                        </Link>
                        {app.sidebar.isOpen && (
                          <div
                            className="workspace-tab-controls"
                            style={{
                              position: "absolute",
                              right: 8,
                              top: "50%",
                              transform: "translateY(-50%)",
                              zIndex: 1,
                              background: "var(--color-bg-base-3)",
                              borderRadius: "var(--border-radius-sm)",
                              border: "1px solid rgba(255,255,255,0.3)",
                            }}
                          >
                            <Button
                              type="text"
                              shape="circle"
                              size="small"
                              style={{ color: "inherit" }}
                              onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                app.appendTab(
                                  tab.current_title,
                                  tab.current_url,
                                  { open: true }
                                );
                              }}
                            >
                              <Tooltip
                                title="Duplicate Tab"
                                mouseEnterDelay={0.5}
                              >
                                <CopyOutlined style={{ fontSize: 10 }} />
                              </Tooltip>
                            </Button>
                            <Button
                              type="text"
                              shape="circle"
                              size="small"
                              style={{ color: "inherit" }}
                              onClick={(e: React.MouseEvent) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (app.activeTabId !== tab.id) {
                                  app.removeTab(tab.id);
                                }
                              }}
                            >
                              <Tooltip title="Close Tab" mouseEnterDelay={0.5}>
                                <CloseOutlined style={{ fontSize: 10 }} />
                              </Tooltip>
                            </Button>
                          </div>
                        )}
                      </div>
                    </SidebarItem>
                  ))}
                </div>
              )}

              {/* Spacer to push Help/Connected to the bottom of scrollable area */}
              <div style={{ flex: 1 }} />
              {/* Help and Connected sections at flex-end */}
              {app.sidebar.isOpen && (
                <>
                  <div style={{ padding: "0 0 8px 0" }}>
                    <div className="section-title section-spacer">
                      Need Help?
                    </div>
                    <div className="section-divider" />
                    <div>
                      <MenuLink
                        icon={
                          <SlackIcon
                            style={{ marginRight: 8, color: token.colorWhite }}
                          />
                        }
                        label="Slack community"
                        href="https://join.slack.com/t/vzblcommunity/shared_invite/zt-2b0jtj2eb-dbKgMi9qzPR~JF1KNQOMvQ"
                        target="_blank"
                      />
                      <MenuLink
                        icon={
                          <QuestionCircleOutlined
                            style={{ marginRight: 8, color: token.colorWhite }}
                          />
                        }
                        label="Help documentation"
                        href="https://vzbl.notion.site/VZBL-1b1dce49eeb4801c929afb325f5ced36"
                        target="_blank"
                      />
                      <MenuLink
                        icon={
                          <FileTextOutlined
                            style={{ marginRight: 8, color: token.colorWhite }}
                          />
                        }
                        label="Release notes"
                        href="https://www.vzbl.dev/release-notes"
                        target="_blank"
                      />
                    </div>
                  </div>
                  <div>
                    <div className="section-title section-spacer">
                      Connected
                    </div>
                    <div className="section-divider" />
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: 16,
                      }}
                    >
                      {integrations.data
                        .filter(
                          (integration) => integration.status === "success"
                        )
                        .sort((a, b) => a.name.localeCompare(b.name))
                        .map((integration) => (
                          <Tooltip
                            key={integration.id}
                            title={integration.name}
                            placement="right"
                          >
                            <Link
                              href={{
                                pathname:
                                  "/[orgId]/settings/integrations/[source]",
                                query: {
                                  orgId: org_id,
                                  source: integration.id,
                                },
                              }}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                              }}
                            >
                              <div
                                className="menu-item"
                                style={{
                                  margin: "0 2px",
                                  fontSize: 22,
                                  borderRadius: 6,
                                  padding: 4,
                                  cursor: "pointer",
                                  transition: "background 0.2s, transform 0.2s",
                                }}
                                onMouseOver={(e) =>
                                  (e.currentTarget.style.background =
                                    "rgba(255,255,255,0.08)")
                                }
                                onMouseOut={(e) =>
                                  (e.currentTarget.style.background =
                                    "transparent")
                                }
                              >
                                <integration.Icon />
                              </div>
                            </Link>
                          </Tooltip>
                        ))}
                      {integrations.data.some(
                        (integration) => integration.status !== "success"
                      ) && (
                        <Tooltip
                          title={
                            <div style={{ maxWidth: "200px" }}>
                              <div
                                style={{ marginBottom: "8px", fontWeight: 500 }}
                              >
                                Setup Required:
                              </div>
                              {integrations.data
                                .filter(
                                  (integration) =>
                                    integration.status !== "success"
                                )
                                .map((integration) => (
                                  <div
                                    key={integration.id}
                                    style={{ marginBottom: "4px" }}
                                  >
                                    • {integration.name}
                                  </div>
                                ))}
                            </div>
                          }
                          placement="right"
                        >
                          <Link
                            href={{
                              pathname: "/[orgId]/settings/integrations",
                              query: { orgId: org_id },
                            }}
                            style={{ marginLeft: 8 }}
                          >
                            <div
                              className="menu-item"
                              style={{
                                fontSize: 22,
                                borderRadius: 6,
                                padding: 4,
                                cursor: "pointer",
                                transition: "background 0.2s, transform 0.2s",
                              }}
                              onMouseOver={(e) =>
                                (e.currentTarget.style.background =
                                  "rgba(255,255,255,0.08)")
                              }
                              onMouseOut={(e) =>
                                (e.currentTarget.style.background =
                                  "transparent")
                              }
                            >
                              <QuestionCircleOutlined />
                            </div>
                          </Link>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* Sticky footer section: only notifications, settings, logout */}
            {app.org_id && (
              <div
                className="section-divider"
                style={{
                  borderRadius: 16,
                  borderTopLeftRadius: 0,
                  borderTopRightRadius: 0,
                  display: "flex",
                  fontSize: 10,
                  fontWeight: 600,
                  padding: 8,
                  textTransform: "uppercase",
                  justifyContent: "center",
                  marginTop: "32px",
                  flexShrink: 0,
                }}
              >
                {app.sidebar.isOpen && (
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "flex-start",
                    }}
                  >
                    {app.isOrgAdmin && <Notifications orgId={app.org_id} />}
                    {app.isOrgAdmin && (
                      <Tooltip title="Settings">
                        <Link
                          onClick={() => setMenu("settings")}
                          href={`/${orgId}/settings`}
                          style={{ color: "inherit" }}
                        >
                          <Button
                            type="text"
                            shape="circle"
                            size="small"
                            style={{ color: "inherit" }}
                            icon={<SettingOutlined style={{ fontSize: 12 }} />}
                          />
                        </Link>
                      </Tooltip>
                    )}
                    <Tooltip title="Sign out">
                      <div className="js-menu-signout">
                        <Popconfirm
                          trigger={["click"]}
                          placement="rightTop"
                          title={app.user?.email}
                          description="Would you like to sign out?"
                          showCancel={orgCount > 0}
                          cancelText="Switch Orgs"
                          onCancel={() => showOrgs(true)}
                          onConfirm={() =>
                            signOut({ callbackUrl: window.location.origin })
                          }
                          okText="Yes, sign out"
                          icon={
                            <LogoutOutlined
                              style={{ color: token.colorText }}
                            />
                          }
                        >
                          <Button
                            type="text"
                            shape="circle"
                            size="small"
                            style={{ color: "inherit" }}
                            icon={<LogoutOutlined style={{ fontSize: 12 }} />}
                          />
                        </Popconfirm>
                      </div>
                    </Tooltip>
                  </div>
                )}
              </div>
            )}
          </div>
        </Flex>
        {showingOrgs && <OrgLauncherNew onClose={() => showOrgs(false)} />}
        {showSearchModal && (
          <SearchModal
            open
            onClose={() => setShowSearchModal(false)}
            orgId={org_id || ""}
            topics={topics}
          />
        )}
      </div>
      <style jsx global>{`
        .workspace-tab-controls {
          display: none;
          gap: 4px;
          align-items: center;
          margin-left: 4px;
          position: absolute;
          right: 8px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 1;
          background: none;
        }
        .workspace-tab-row:hover .workspace-tab-controls {
          display: flex;
        }
      `}</style>
    </CssVars>
  );
}

function MainButton({
  loading,
  showCollapsible,
}: {
  loading: boolean;
  showCollapsible: boolean;
}) {
  const { token } = theme.useToken();
  const { isOpen, toggle } = useApplicationContext().sidebar;
  const app = useApplicationContext();
  return (
    <div
      className="js-menu-main"
      style={{
        alignItems: "center",
        borderRadius: 16,
        display: "flex",
        fontSize: 12,
        fontWeight: 500,
        height: "100%",
        justifyContent: isOpen ? "space-between" : "center",
        overflow: isOpen ? undefined : "hidden",
        padding: "12px 8px 8px 16px",
        width: isOpen ? MENU_WIDTH : COLLAPSED_MENU_WIDTH,
      }}
    >
      {isOpen && (
        <Flex gap={12} style={{ alignItems: "center" }}>
          <div>
            <VzblIcon size={24} />
          </div>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: "#fff",
              maxWidth: MENU_WIDTH - 100,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {app.org_name || "Organization"}
          </div>
        </Flex>
      )}
      <Tooltip
        open={isOpen ? false : undefined}
        title="Toggle application sidebar"
        placement="right"
        mouseEnterDelay={isOpen ? 60 : 0}
      >
        <Button
          size="small"
          shape="circle"
          type="text"
          style={{
            color: "inherit",
            border: "none",
            opacity: showCollapsible ? 1 : 0,
            transition: "opacity 0.3s ease, transform 0.3s ease",
          }}
          onClick={() => toggle()}
          icon={<SidebarIcon style={{ marginTop: 4 }} />}
        />
      </Tooltip>
    </div>
  );
}

const SidebarItem = ({
  hoverBgColor,
  disabled,
  isActive,
  children,
  style,
}: PropsWithChildren<{
  hoverBgColor?: string;
  disabled?: boolean;
  isActive?: boolean;
  style?: CSSProperties;
}>) => {
  const token = theme.useToken().token;
  return (
    <div style={{ position: "relative" }}>
      <Hover
        baseStyle={{
          alignItems: "center",
          borderRadius: token.borderRadiusSM,
          cursor: disabled ? "default" : "pointer",
          display: "flex",
          fontSize: 13,
          fontWeight: isActive ? 600 : 400,
          height: "100%",
          margin: 1,
          minHeight: 32,
          opacity: disabled ? 0.3 : undefined,
          paddingBottom: 3,
          paddingTop: 3,
          position: "relative",
          ...style,
        }}
        hoverStyle={{
          backgroundColor: disabled
            ? "inherit"
            : isActive
            ? "inherit"
            : hoverBgColor || token.colorBgMask,
          opacity: disabled ? 0.3 : 1,
          transition: "all .1s",
        }}
      >
        <div>{children}</div>
      </Hover>
      {isActive && (
        <div
          style={{
            backgroundColor: token.colorTextSecondary,
            borderBottomRightRadius: 5,
            borderTopRightRadius: 5,
            height: "100%",
            left: -8,
            position: "absolute",
            top: 0,
            width: 3,
          }}
        />
      )}
    </div>
  );
};

export const MenuLink = (
  props: PropsWithChildren<{
    href?: string | { pathname: string; query: {} };
    disabled?: boolean;
    icon?: any;
    label: ReactNode;
    linkTitle?: string;
    searchState?: Partial<SearchState>;
    viz?: Partial<SerializableView>;
    root?: boolean;
    hoverBgColor?: string;
    isActive?: boolean;
    onClick?: (event: MouseEvent) => void;
    target?: string;
  }>
) => {
  const app = useApplicationContext();
  const orgId = app.org_id;
  const open = !props.root || app.sidebar.isOpen;
  const token = theme.useToken().token;
  const title =
    props.linkTitle ||
    (typeof props.label === "string" ? props.label : "missing title");
  const queryString = {
    s: serializeParams(props.searchState),
    t: serializeTabs({}),
    d: serializeDocument({ title }),
    v: serializeContentView(),
  };
  const goingToExplore =
    typeof props.href === "string" ? props.href.endsWith("/explore") : false;
  const getExploreContext = globalFunction(GlobalFunction.getExploreContext);
  const href =
    goingToExplore && props.searchState
      ? typeof props.href === "string"
        ? { pathname: props.href, query: queryString }
        : props.href
      : props.href;

  const handleClick = (e: MouseEvent) => {
    if (props.onClick) {
      props.onClick(e);
      if (e.defaultPrevented) {
        return;
      }
    }

    if (goingToExplore && getExploreContext) {
      e.preventDefault();
      e.stopPropagation();
      const context: ExploreContext = getExploreContext();
      const serialized = serializeParams(
        ensureState(props.searchState, orgId || "")
      );
      if (props.searchState) {
        context.doc.open({
          id: undefined,
          saved: false,
          title,
          serialized,
          viz: {
            value: ContentViews.default,
            config: {},
            ...props.viz,
          },
        });
      }
    }
  };

  return (
    <SidebarItem
      hoverBgColor={props.hoverBgColor}
      isActive={props.root && props.isActive}
      disabled={props.disabled}
      style={
        props.root
          ? {
              paddingTop: 0,
              paddingBottom: 0,
            }
          : undefined
      }
    >
      <Tooltip
        open={
          props.disabled || !props.root || app.sidebar.isOpen
            ? false
            : undefined
        }
        title={props.label}
        placement="right"
        mouseEnterDelay={!props.root || app.sidebar.isOpen ? 60 : 0}
      >
        <Link
          className={`main-menu-item ${
            !props.disabled && href ? "js-menu-link" : undefined
          }`}
          href={(!props.disabled && href) || "#"}
          target={props.target}
          style={{
            flex: 1,
            textDecoration: "none",
            display: "block",
            cursor: props.disabled ? "default" : "pointer",
            height: "100%",
            width: "100%",
            color: props.isActive ? token.colorWhite : "inherit",
          }}
          onClick={handleClick}
        >
          <Flex
            gap={8}
            style={{
              padding: "4px 8px 4px 8px",
              cursor: undefined,
              justifyContent: "flex-start",
              alignItems: "center",
              height: "100%",
            }}
          >
            {props.icon && (
              <Flex centered style={{ width: 20, opacity: 0.6 }}>
                {props.icon}
              </Flex>
            )}
            {open && (
              <span style={{ flex: 1, minWidth: 0 }} className="trunc">
                {props.label}
              </span>
            )}
          </Flex>
        </Link>
      </Tooltip>
    </SidebarItem>
  );
};

function FavoritesMenuWrapper({
  orgId,
  disabled,
}: {
  orgId: string;
  disabled?: boolean;
}) {
  const { spacesQuery } = useSpacesQuery();
  const pinnedSpaces = (spacesQuery.data || [])
    .filter((space) => space.pin_to_nav)
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  const favorites = useLibraryWithoutRouter({
    prefilter(item: Item) {
      if (item.itemType === ItemType.BOARDS)
        return Boolean((item as BoardItem).isBookmarked);
      if (item.itemType === ItemType.QUERIES)
        return Boolean((item as QueryItem).isBookmarked);
      if (item.itemType === ItemType.TOPICS)
        return Boolean((item as TopicItem).isBookmarked);
      if (item.itemType === ItemType.REPORTS)
        return Boolean((item as ReportItem).isBookmarked);
      return false; // Spaces and Streams are not bookmarkable
    },
  });

  return (
    <FavoritesMenu
      orgId={orgId}
      pinnedSpaces={pinnedSpaces}
      libraryItems={favorites.items}
      disabled={disabled}
    />
  );
}
